/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TablePermissionDialog
 *
 * Props:
 * - open: boolean → show/hide modal
 * - onClose: function → called on Cancel / close
 * - applicationId: number|string → current application id (for table list API)
 * - user: object → { id, username?, email? } application user
 * - permission: object|null → existing table-permission row for edit mode
 *      {
 *        id,
 *        application_table,
 *        application_table_name,
 *        application_table_display_name,
 *        permissions: [...],
 *        default_inherit: true/false
 *      }
 *
 * Behavior:
 * - If `permission` is null/undefined → create mode (Grant Permission)
 *   • User can choose table from dropdown
 * - If `permission` is provided → edit mode
 *   • Table is fixed (dropdown disabled)
 *   • Permissions pre-filled from `permission.permissions`
 */
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigate } from 'react-router-dom';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import ProgressBarDialog from '../common/ProgressBar';
import AlertDialog from '../common/AlertDialog';
import Button from '../Button';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const PERMISSION_OPTIONS = [
    { key: 'read', label: 'Read' },
    { key: 'create', label: 'Create' },
    { key: 'update', label: 'Update' },
    { key: 'delete', label: 'Delete' },
];

function TablePermissionDialog({ open, onClose, applicationId, user, permission, onSaved }) {
    const { theme } = useTheme();
    const navigate = useNavigate();

    const isEditMode = !!permission;

    const [tables, setTables] = useState([]);
    const [tablesLoading, setTablesLoading] = useState(false);

    const [selectedTableId, setSelectedTableId] = useState(null);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [hideTable, setHideTable] = useState(true);

    const [saving, setSaving] = useState(false);
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('info');
    const [alertSource, setAlertSource] = useState(null);

    const [tableDropdownOpen, setTableDropdownOpen] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (!open) return;

        // initial permissions & table selection
        if (isEditMode && permission) {
            setSelectedTableId(permission.application_table);
            const perms = permission.permissions || [];
            setSelectedPermissions(perms);
            // If permission.permissions is empty or null, treat that as "hidden"
            setHideTable(!perms || perms.length === 0);
        } else {
            setSelectedTableId(null);
            setSelectedPermissions([]);
            setHideTable(true); // default ON in create mode
        }
    }, [open, isEditMode, permission]);

    // Fetch tables when modal opens
    useEffect(() => {
        if (!open || !applicationId) return;

        let isCancelled = false;

        async function fetchTables() {
            setTablesLoading(true);

            setProgressMessage('Loading tables...');
            setProgressOpen(true);

            try {
                const url = `/arcee/application/table/list/?application_id=${applicationId}&page_size=5000`;
                const resp = await apiHelper.apiCall(url, { method: 'GET' });

                if (!resp.ok) {
                    let errBody;
                    try {
                        errBody = await resp.json();
                    } catch {
                        errBody = await resp.text().catch(() => null);
                    }
                    if (isCancelled) return;

                    const msg =
                        typeof errBody === 'string'
                            ? errBody
                            : 'Failed to load tables.';

                    setTables([]);
                    setProgressOpen(false);
                    setAlertTitle('Failed to load tables');
                    setAlertMessage(msg);
                    setAlertType('error');
                    setAlertSource('load');
                    setAlertOpen(true);
                    return;
                }

                const data = await resp.json().catch(() => []);
                if (isCancelled) return;

                const list = Array.isArray(data)
                    ? data
                    : data.results ?? data ?? [];

                setTables(list);
            } catch (err) {
                if (isCancelled) return;

                const msg = err.message || 'Could not load tables.';
                setTables([]);
                setProgressOpen(false);
                setAlertTitle('Failed to load tables');
                setAlertMessage(msg);
                setAlertType('error');
                setAlertSource('load');
                setAlertOpen(true);
                return;
            } finally {
                if (!isCancelled) {
                    setTablesLoading(false);
                    setProgressOpen(false);
                }
            }
        }

        fetchTables();

        return () => {
            isCancelled = true;
        };
    }, [open, applicationId]);

    // Reset state when modal opens
    useEffect(() => {
        if (!open) return;

        if (isEditMode && permission) {
            setSelectedTableId(permission.application_table);
            const perms = permission.permissions || [];
            setSelectedPermissions(perms);
            setHideTable(!perms || perms.length === 0);

        } else {
            setSelectedTableId(null);
            setSelectedPermissions([]);
            setHideTable(true);
        }
    }, [open, isEditMode, permission]);

    if (!open) return null;

    const handleCheckboxChange = (key) => {
        setSelectedPermissions((prev) => {
            let next;
            if (prev.includes(key)) {
                next = prev.filter((p) => p !== key);
            } else {
                next = [...prev, key];
            }

            // If any permission is selected -> hideTable must be false
            if (next.length > 0) {
                if (hideTable) setHideTable(false);
            } else {
                // No permissions -> auto-enable hideTable
                if (!hideTable) setHideTable(true);
            }

            return next;
        });
    };

    const handleHideChange = () => {
        // toggle hide
        setHideTable((prev) => {
            const nextHide = !prev;
            if (nextHide) {
                // when hiding, clear other permissions (payload must become [])
                setSelectedPermissions([]);
            } else {
                // when un-hiding, select no permissions by default (user can choose)
                // Optionally you could set all perms selected here — we keep empty so user picks.
                setSelectedPermissions([]);
            }
            return nextHide;
        });
    };

    const formatServerErrors = (errBody) => {
        if (!errBody) return 'Something went wrong. Please try again.';
        if (typeof errBody === 'string') return errBody;
        if (typeof errBody === 'object') {
            const parts = [];
            for (const [k, v] of Object.entries(errBody)) {
                if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
                else parts.push(`${k}: ${String(v)}`);
            }
            return parts.join('\n');
        }
        return 'Something went wrong. Please try again.';
    };

    const handleSave = async () => {
        if (!user || !user.id) {
            setAlertTitle('Invalid User');
            setAlertMessage('Application user information is missing.');
            setAlertType('error');
            setAlertSource('save');
            setAlertOpen(true);
            return;
        }

        if (!selectedTableId) {
            setAlertTitle('Missing Table');
            setAlertMessage('Please select a table before saving.');
            setAlertType('error');
            setAlertSource('save');
            setAlertOpen(true);
            return;
        }

        setSaving(true);
        setProgressMessage('Saving table permissions...');
        setProgressOpen(true);

        const bodyPayload = {
            application_user_id: Number(user.id),
            application_table_id: Number(selectedTableId),
            permissions: hideTable ? [] : selectedPermissions,
            default_inherit: true,
        };

        try {
            const resp = await apiHelper.apiCall('/arcee/user/table-permissions/grant/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyPayload),
            });

            setProgressOpen(false);

            if (!resp.ok) {
                let errBody;
                try {
                    errBody = await resp.json();
                } catch {
                    errBody = await resp.text().catch(() => null);
                }

                const message = formatServerErrors(errBody);
                setAlertTitle('Failed to update permissions');
                setAlertMessage(message);
                setAlertType('error');
                setAlertSource('save');
                setAlertOpen(true);
                return;
            }

            const data = await resp.json().catch(() => ({}));
            const successMsg =
                data.message ||
                (isEditMode
                    ? 'Permissions updated successfully.'
                    : 'Permissions granted successfully.');

            setAlertTitle('Success');
            setAlertMessage(successMsg);
            setAlertType('success');
            setAlertSource('save');
            setAlertOpen(true);
        } catch (err) {
            setProgressOpen(false);
            setAlertTitle('Network Error');
            setAlertMessage(err.message || 'Unable to connect to server. Please try again later.');
            setAlertType('error');
            setAlertSource('save');
            setAlertOpen(true);
        } finally {
            setSaving(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);

        // If error happened while loading tables → close modal
        if (alertSource === 'load') {
            setAlertSource(null);
            onClose && onClose();
            return;
        }

        // On successful save → call onSaved (to refetch list) and close modal
        if (alertType === 'success' && alertSource === 'save') {
            if (onSaved) {
                onSaved();
            }
            setAlertSource(null);
            onClose && onClose();
            return;
        }

        setAlertSource(null);
    };

    const handleCancel = () => {
        if (saving) return;
        setTableDropdownOpen(false);
        onClose && onClose();
    };

    // styles
    const backdropStyle = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 3000,
    };

    const modalStyle = {
        width: 520,
        maxWidth: '95vw',
        background: '#fff',
        borderRadius: theme.cardRadius,
        boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
        padding: '1.5rem 1.75rem 1.25rem',
        position: 'relative',
    };

    const titleText = isEditMode ? 'Edit Table Permission' : 'Grant Table Permission';

    const currentTableDisplayName =
        (isEditMode && permission?.application_table_display_name) ||
        (isEditMode && permission?.application_table_name) ||
        '';

    const selectedTable = tables.find((t) => t.id === selectedTableId);
    const selectedLabel = selectedTable
        ? (selectedTable.display_name || selectedTable.name)
        : '';

    return (
        <>
            <div style={backdropStyle} onClick={handleCancel}>
                <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0" style={{ color: theme.primary, fontWeight: 600 }}>
                            {titleText}
                        </h5>
                        <button
                            type="button"
                            onClick={handleCancel}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 20,
                                lineHeight: 1,
                                color: theme.primary,
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>

                    {/* Table dropdown (custom, scrollable) */}
                    <div className="mb-3">
                        <div className="alert alert-info small border-0 bg-light-info text-dark mb-4" style={{ backgroundColor: '#e2f2ff' }}>
                            <div className="d-flex">
                                <i className="bi bi-info-circle-fill text-info me-2 mt-1"></i>
                                <div>
                                    <strong>Access Control Layers:</strong><br />
                                    1. <strong>RLS (Group Assign)</strong>: Controls <em>which tables they have access to and which records</em> a user sees.<br />
                                    2. <strong>Table Permissions</strong>: Controls <em>actions</em> (Read, Write) on those tables and records.<br />
                                    <span className="text-decoration-underline">Both are required for access.</span>
                                </div>
                            </div>
                        </div>

                        <label
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Table <span style={{ color: '#c80b23' }}> *</span>
                        </label>

                        {isEditMode ? (
                            // In edit mode: show read-only text input (cannot change table)
                            <input
                                type="text"
                                className="form-control"
                                value={currentTableDisplayName || `Table #${selectedTableId}`}
                                disabled
                                style={{
                                    borderRadius: theme.buttonRadius,
                                    borderColor: theme.primary + '55',
                                    backgroundColor: '#f3f3f3',
                                    cursor: 'not-allowed',
                                }}
                            />
                        ) : (
                            // Create mode: custom dropdown
                            <div style={{ position: 'relative' }}>
                                {/* Trigger */}
                                <div
                                    onClick={() => setTableDropdownOpen((open) => !open)}
                                    style={{
                                        borderRadius: theme.buttonRadius,
                                        border: `1px solid ${theme.primary}55`,
                                        padding: '6px 10px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        background: '#fff',
                                    }}
                                >
                                    <span style={{ color: selectedTableId ? '#000' : '#777' }}>
                                        {selectedTableId ? selectedLabel : 'Select a table'}
                                    </span>
                                    <i
                                        className={`bi bi-chevron-${tableDropdownOpen ? 'up' : 'down'}`}
                                        style={{ color: theme.primary }}
                                    />
                                </div>

                                {/* Dropdown menu */}
                                {tableDropdownOpen && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            marginTop: 4,
                                            background: '#fff',
                                            borderRadius: theme.buttonRadius,
                                            border: `1px solid ${theme.primary}33`,
                                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            zIndex: 2000,
                                        }}
                                    >
                                        {tables.length === 0 ? (
                                            <div
                                                style={{
                                                    padding: '8px 10px',
                                                    fontSize: 14,
                                                    color: '#888',
                                                }}
                                            >
                                                No tables found.
                                            </div>
                                        ) : (
                                            tables.map((t) => {
                                                const label = t.display_name || t.name;
                                                const isSelected = selectedTableId === t.id;
                                                return (
                                                    <div
                                                        key={t.id}
                                                        onClick={() => {
                                                            setSelectedTableId(t.id);
                                                            setTableDropdownOpen(false);
                                                        }}
                                                        style={{
                                                            padding: '8px 10px',
                                                            cursor: 'pointer',
                                                            background: isSelected ? theme.primary + '11' : '#fff',
                                                            color: isSelected ? theme.primary : '#333',
                                                        }}
                                                    >
                                                        {label}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Permissions checkboxes */}
                    <div className="mb-3">
                        <label
                            className="form-label fw-semibold"
                            style={{ color: theme.primary, fontSize: 15 }}
                        >
                            Permissions
                        </label>
                        <div className="d-flex flex-wrap" style={{ gap: '10px 16px' }}>
                            {PERMISSION_OPTIONS.map((perm) => (
                                <label
                                    key={perm.key}
                                    className="form-check-label"
                                    style={{ fontSize: 14, cursor: 'pointer' }}
                                >
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-1"
                                        checked={selectedPermissions.includes(perm.key)}
                                        onChange={() => handleCheckboxChange(perm.key)}
                                        style={{ cursor: 'pointer', marginRight: 6 }}
                                    />
                                    {perm.label}
                                </label>
                            ))}
                            {/* "Hide table from user" checkbox */}
                            <div style={{ marginTop: 8 }}>
                                <label className="form-check-label" style={{ fontSize: 14, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-1"
                                        checked={hideTable}
                                        onChange={handleHideChange}
                                        style={{ cursor: 'pointer', marginRight: 6 }}
                                    />
                                    Hide table from user
                                </label>
                                <div style={{ fontSize: 12, color: '#777', marginTop: 6 }}>
                                    When checked, the user will have no explicit permissions on this table
                                    (permissions will be submitted as an empty list). If you uncheck this,
                                    choose explicit permissions above to grant access.
                                </div>
                            </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                            Leave all unchecked to inherit access from the user&apos;s role.
                        </div>
                    </div>



                    {/* Footer buttons */}
                    <div className="d-flex justify-content-end" style={{ gap: 10 }}>
                        <Button
                            mode="outlined"
                            type="button"
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            mode="primary-bold"
                            type="button"
                            onClick={handleSave}
                            disabled={saving || tablesLoading}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Progress while loading tables / saving */}
            <ProgressBarDialog
                open={progressOpen}
                text_message={progressMessage}
                cancellable={false}
            />

            {/* Alert for success / error */}
            <AlertDialog
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                okLabel="OK"
                showCancel={false}
                onOk={handleAlertClose}
                onCancel={() => setAlertOpen(false)}
                mini_mode={false}
            />
        </>
    );
}

export default TablePermissionDialog;
